import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  LayoutDashboard, BookOpen, Users, GitBranch, Globe,
  BarChart3, MessageSquare, Megaphone, Settings, CreditCard,
  CalendarDays, ChevronLeft, ChevronRight, Bot,
} from 'lucide-react'

const navItems = [
  { to: '/dashboard',     label: 'Dashboard',      icon: LayoutDashboard },
  { to: '/schedule',      label: 'Schedule',        icon: CalendarDays },
  { to: '/courses',       label: 'Courses',         icon: BookOpen },
  { to: '/users',         label: 'All Users',        icon: Users },
  { to: '/paths',         label: 'Learning Paths',  icon: GitBranch },
  { to: '/website',       label: 'Website',          icon: Globe },
  { to: '/reports',       label: 'Reports Center',  icon: BarChart3 },
  { to: '/communication', label: 'Communication',   icon: MessageSquare },
  { to: '/zalo',          label: 'Zalo Bot',        icon: Bot },
  { to: '/marketing',     label: 'Marketing',        icon: Megaphone },
  { to: '/settings',      label: 'Settings',         icon: Settings },
  { to: '/subscriptions', label: 'Subscriptions',   icon: CreditCard },
]

interface SidebarProps {
  collapsed?: boolean
  onCollapse?: (v: boolean) => void
}

export default function Sidebar({ collapsed = false, onCollapse }: SidebarProps) {
  return (
    <aside
      className={cn(
        'hidden md:flex flex-shrink-0 flex-col bg-sidebar transition-all duration-200',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      {/* Logo */}
      <div className={cn('flex h-16 items-center border-b border-sidebar-border', collapsed ? 'justify-center px-2' : 'px-5')}>
        {collapsed ? (
          <span className="text-lg font-bold text-sidebar-foreground">U</span>
        ) : (
          <span className="text-xl font-bold text-sidebar-foreground tracking-tight">Uyendo</span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors min-h-[40px]',
                collapsed && 'justify-center px-2',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
              )
            }
          >
            <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Collapse toggle */}
      {onCollapse && (
        <div className="border-t border-sidebar-border p-2">
          <button
            onClick={() => onCollapse(!collapsed)}
            className={cn(
              'flex h-9 w-full items-center gap-2 rounded-md px-3 text-sm text-sidebar-foreground/60',
              'hover:bg-sidebar-accent/60 hover:text-sidebar-foreground transition-colors',
              collapsed && 'justify-center',
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : (
              <>
                <ChevronLeft className="h-4 w-4" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </aside>
  )
}

/* Mobile sidebar — exported for use in MobileNav */
export function MobileSidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      {/* Drawer */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 flex flex-col bg-sidebar transition-transform duration-200 md:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Navigation"
      >
        <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-5">
          <span className="text-xl font-bold text-sidebar-foreground">Uyendo</span>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground"
            aria-label="Close navigation"
          >
            ✕
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px]',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
                )
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  )
}
